<template>
  <div
    class="mt-3"
    v-if="
      product &&
      product.has_variations &&
      product.product_options &&
      product.product_options.length
    "
  >
    <div v-for="option in product.product_options" :key="option.id">
      <h6 class="mb-3 text-capitalize">{{ option.name }}</h6>
      <v-row dense class="mb-3">
        <v-col
          cols="12"
          sm="6"
          md="4"
          lg="3"
          v-for="(value, index) in option.values || []"
          :key="index"
          v-show="isOptionAvailable(option.name, value)"
        >
          <v-card
            @click="selectOption(option.name, value)"
            outlined
            class="option-card"
            :class="{
              selected: isOptionSelected(option.name, value),
              'in-cart': isInCart(option.name, value),
            }"
          >
            <v-card-text class="pa-5">
              <div class="text-center align-center">
                <div class="font-weight-bold text-capitalize black--text">
                  {{ value }}
                  <v-icon
                    v-if="isInCart(option.name, value)"
                    small
                    color="success"
                    class="ml-1"
                    >fal fa-check-circle</v-icon
                  >
                </div>
                <div
                  v-if="isInCart(option.name, value)"
                  class="in-cart-label mt-2"
                >
                  <v-chip x-small color="success" outlined>{{
                    $t("in_purchase_list")
                  }}</v-chip>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import { myMixin } from "@/mixin/main";
export default {
  name: "ProductVariationDisplay",
  mixins: [myMixin],
  props: {
    product: { type: Object, required: true },
    selectedOptions: { type: Object, default: () => ({}) },
    readOnly: { type: Boolean, default: false },
    cartItems: { type: Array, default: () => [] },
    // Optionally, a callback for editing a cart item
    onEditCartItem: { type: Function, default: null },
  },
  data() {
    return { localSelectedOptions: { ...this.selectedOptions } };
  },
  watch: {
    selectedOptions: {
      immediate: true,
      handler(newVal) {
        this.localSelectedOptions = { ...newVal };
      },
    },
  },
  methods: {
    selectOption(optionName, value) {
      if (this.readOnly) return;
      this.$set(this.localSelectedOptions, optionName, value);
      this.$emit("option-selected", {
        optionName,
        value,
        allSelectedOptions: { ...this.localSelectedOptions },
      });
    },
    isOptionSelected(optionName, value) {
      return this.localSelectedOptions[optionName] === value;
    },
    isOptionAvailable(optionName, value) {
      if (!this.product.variants) return false;

      // Always show all values for the current option type
      // This allows users to switch between options (e.g., 128GB ↔ 512GB)
      if (this.localSelectedOptions[optionName] === value) {
        return true;
      }

      // For the current option type, always show all available values
      // Check if ANY variant exists with this option value
      const hasAnyVariantWithThisValue = this.product.variants.some(
        (variant) => variant.options && variant.options[optionName] === value
      );

      if (hasAnyVariantWithThisValue) {
        // If track_stock is enabled, check if any variant with this value has stock
        if (
          this.product.track_stock === 1 ||
          this.product.track_stock === "1"
        ) {
          const hasStock = this.product.variants.some(
            (variant) =>
              variant.options &&
              variant.options[optionName] === value &&
              variant.stock > 0
          );
          return hasStock;
        }
        return true; // If track_stock is disabled, show all existing values
      }

      return false; // No variant exists with this option value
    },
    isInCart(optionName, value) {
      // Mark as in cart if ANY cart item for this product has a variant with this option value
      if (!this.cartItems || !this.product.variants) return false;
      // Find all variants for this product with this option value
      const matchingVariants = this.product.variants.filter(
        (v) => v.options && v.options[optionName] === value
      );
      if (!matchingVariants.length) return false;
      // Check if any of these variants are in the cart
      return this.cartItems.some(
        (item) =>
          item.variant &&
          matchingVariants.some((v) => v.sku === item.variant.sku)
      );
    },
  },
};
</script>

<style scoped>
.option-card {
  border-radius: 8px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  border: 2px solid #e0e0e0;
  transition: background 0.2s, border-color 0.2s;
}
.option-card:hover {
  border-color: #5b39c9;
}
.option-card.selected {
  border-color: #5b39c9;
  background: #5b39c921;
}
.option-card.in-cart {
  border-color: #43a047;
  background: #e8f5e9;
  position: relative;
}

.option-card.in-cart::before {
  content: "✓";
  position: absolute;
  top: 8px;
  right: 8px;
  background: #43a047;
  color: white;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  z-index: 1;
}

.in-cart-label {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9em;
}
</style>
